import type { ByteSink } from '@mimir/common/format/mp4Subclip';
import { invariant } from '@mimir/common/util/invariant';

/*
  Destinations for a muxed download. The muxer streams fragment-by-fragment into
  a ByteSink, so both sinks below bound peak memory to a single fragment; they
  differ only in where the bytes end up. The File System Access API types are not
  yet in the standard DOM lib, so we model only the slice we use and read the
  picker off `window` defensively. This keeps that path optional: callers get null
  on browsers that do not support it and use the blob fallback instead.
*/

interface WritableFileStream {
  write(data: BufferSource): Promise<void>;
  close(): Promise<void>;
  abort(reason: string): Promise<void>;
}

interface SaveFileHandle {
  createWritable(): Promise<WritableFileStream>;
}

interface SaveFilePickerOptions {
  suggestedName: string;
}

type ShowSaveFilePicker = (options: SaveFilePickerOptions) => Promise<SaveFileHandle>;

function getShowSaveFilePicker(): ShowSaveFilePicker | null {
  const picker = (window as unknown as { showSaveFilePicker?: ShowSaveFilePicker }).showSaveFilePicker;
  return typeof picker === 'function' ? picker : null;
}

/**
 * A muxed-download destination: a ByteSink that is finalized with close() on
 * success or abort() on failure/cancellation.
 */
export interface DownloadSink extends ByteSink {
  close(): Promise<void>;
  abort(reason: string): Promise<void>;
}

/**
 * Prompt for a save location and return a sink that streams straight to that
 * file (nothing accumulates in memory).
 *
 * Must be invoked within a user gesture: the picker requires transient
 * activation. Returns null when the API is unavailable. Rejects (with an
 * AbortError) when the user dismisses the picker.
 */
export async function openFileSaveSink(suggestedName: string): Promise<DownloadSink | null> {
  const picker = getShowSaveFilePicker();
  if (picker == null) {
    return null;
  }
  try {
    const handle = await picker({ suggestedName });
    const writable = await handle.createWritable();
    return {
      write: (chunk: Uint8Array) => writable.write(chunk),
      close: () => writable.close(),
      abort: (reason: string) => writable.abort(reason),
    };
  } catch (ex: unknown) {
    // User dismissed the picker.
    if ((ex as { name?: unknown } | null)?.name === 'AbortError') {
      throw ex;
    }
    // NotAllowedError/SecurityError/etc: allow callers to fall back to blob download.
    return null;
  }
}

/**
 * Fallback sink for browsers without the File System Access API. Each written
 * fragment is wrapped in a Blob immediately so the JS heap never accumulates the
 * whole output; the fragments live in browser blob storage (disk-backed) until
 * close() assembles them and hands the finished file to `deliver` for a normal
 * anchor download. `deliver` is injected so this stays DOM-free and unit-testable.
 */
export function createBlobDownloadSink(
  suggestedName: string,
  deliver: (blob: Blob, filename: string) => void,
): DownloadSink {
  let parts: Blob[] | null = [];
  return {
    write: (chunk: Uint8Array) => {
      invariant(parts, 'write after close/abort');
      parts.push(new Blob([chunk]));
      return Promise.resolve();
    },
    close: () => {
      invariant(parts, 'close after close/abort');
      const blob = new Blob(parts);
      parts = null;
      deliver(blob, suggestedName);
      return Promise.resolve();
    },
    abort: () => {
      parts = null;
      return Promise.resolve();
    },
  };
}
